import {
  zonedDateString,
  zonedEvent,
  zonedWallTimeToUtc,
} from '@/lib/timezone';
import { format, parse } from 'date-fns';
import { useEffect, useState } from 'react';

const HOURS = Array.from({ length: 24 }, (_, value) => value);
const MINUTES = Array.from({ length: 60 }, (_, value) => value);
const pad = (value: number) => String(value).padStart(2, '0');

export function isFutureSchedule(
  date: string,
  time: string,
  timeZone: string,
  now = Date.now()
) {
  if (!date || !/^\d{2}:\d{2}$/.test(time)) return false;
  const [hour, minute] = time.split(':').map(Number);
  if (hour > 23 || minute > 59) return false;
  const day = parse(date, 'yyyy-MM-dd', new Date());
  const instant = zonedWallTimeToUtc(day, hour, minute, timeZone);
  if (!(instant.getTime() > now)) return false;
  // Reject nonexistent wall times during a daylight-saving clock jump too.
  const wall = zonedEvent(instant.toISOString(), timeZone);
  return format(wall, 'yyyy-MM-dd') === date && format(wall, 'HH:mm') === time;
}

/** Boundaries follow the business timezone, just like the update payload. */
export function useFutureSchedule(
  date: string,
  time: string,
  timeZone: string
) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);
  const earliest = new Date(Math.floor(now / 60_000) * 60_000 + 60_000);
  const minDate = parse(
    zonedDateString(earliest, timeZone),
    'yyyy-MM-dd',
    new Date()
  );
  const selectDate = (day: Date | undefined) => {
    if (!day) return undefined;
    const currentTime = Date.now();
    const chosen = format(day, 'yyyy-MM-dd');
    const firstDay = zonedDateString(
      new Date(Math.floor(currentTime / 60_000) * 60_000 + 60_000),
      timeZone
    );
    if (chosen < firstDay) return undefined;
    if (isFutureSchedule(chosen, time, timeZone, currentTime))
      return { date: chosen, time };
    for (const hour of HOURS) {
      for (const minute of MINUTES) {
        const candidate = `${pad(hour)}:${pad(minute)}`;
        if (isFutureSchedule(chosen, candidate, timeZone, currentTime))
          return { date: chosen, time: candidate };
      }
    }
    return undefined;
  };
  return {
    now,
    minDate,
    selectDate,
    isFuture: isFutureSchedule(date, time, timeZone, now),
  };
}

export function FutureTimeSelect({
  id,
  value,
  date,
  timeZone,
  now,
  onChange,
  disabled,
  className,
}: {
  id: string;
  value: string;
  date: string;
  timeZone: string;
  now: number;
  onChange: (value: string) => void;
  disabled?: boolean;
  className?: string;
}) {
  const [hour, minute] = value ? value.split(':').map(Number) : [];
  const allowed = (h: number, m: number, at = now) =>
    isFutureSchedule(date, `${pad(h)}:${pad(m)}`, timeZone, at);
  const select = (h: number, m: number) => {
    if (allowed(h, m, Date.now())) onChange(`${pad(h)}:${pad(m)}`);
  };
  const styles =
    className ??
    'h-10 w-full rounded-md border border-input bg-background px-3 text-sm disabled:opacity-50';
  return (
    <div className="flex items-center gap-1">
      <select
        id={id}
        aria-label="Hour to post"
        value={hour ?? ''}
        disabled={disabled || !date}
        className={styles}
        onChange={(event) => {
          const h = Number(event.target.value);
          const at = Date.now();
          const m =
            minute !== undefined && allowed(h, minute, at)
              ? minute
              : MINUTES.find((candidate) => allowed(h, candidate, at));
          if (m !== undefined) select(h, m);
        }}
      >
        <option value="" disabled>
          HH
        </option>
        {HOURS.map((h) => (
          <option
            key={h}
            value={h}
            disabled={!MINUTES.some((m) => allowed(h, m))}
          >
            {pad(h)}
          </option>
        ))}
      </select>
      <span aria-hidden="true">:</span>
      <select
        aria-label="Minute to post"
        value={minute ?? ''}
        disabled={disabled || !date}
        className={styles}
        onChange={(event) => {
          if (hour !== undefined) select(hour, Number(event.target.value));
        }}
      >
        <option value="" disabled>
          MM
        </option>
        {MINUTES.map((m) => (
          <option
            key={m}
            value={m}
            disabled={hour === undefined || !allowed(hour, m)}
          >
            {pad(m)}
          </option>
        ))}
      </select>
    </div>
  );
}
